/**
 * This viewer's alert feed: every mission alert classified for ME (mission-wide / mine / producer /
 * others) and tiered (important = toast, info = list only). Shared logic: @apos/shared alertFeed.ts.
 */
import { useMemo } from 'react';
import { alertViewer, buildAlertFeed, unreadImportant, type Alert, type AlertFeedItem, type AlertViewer, type LatLon } from '@apos/shared';
import { useLocationSharing } from '../../lib/geolocation';
import { useMission } from '../../state/mission';
import { useAlertSeen } from './alertSeen';

const NO_ALERTS: Alert[] = [];

/** ~100 m grid so the viewer (and the feed) is not rebuilt on every GPS fix. */
const grid = (p: LatLon | null) => (p ? `${p.lat.toFixed(3)},${p.lon.toFixed(3)}` : '');

export function useAlertViewer(): AlertViewer | null {
  const bundle = useMission((s) => s.bundle);
  const sharing = useLocationSharing();
  const me = bundle?.me.userId ?? null;
  const lastObs = useMission((s) => (me ? s.record?.crewObs[me]?.at(-1) : undefined));
  const fix: LatLon | null =
    sharing.lastFix && sharing.missionId === bundle?.mission.id ? { lat: sharing.lastFix.lat, lon: sharing.lastFix.lon } : lastObs && lastObs.lat != null ? { lat: lastObs.lat, lon: lastObs.lon } : null;
  const key = grid(fix);
  return useMemo(
    () => (bundle ? alertViewer(bundle, bundle.me.userId, bundle.myRole, { myPos: fix }) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bundle, key],
  );
}

export function useAlertFeed(): { items: AlertFeedItem[]; viewer: AlertViewer | null; unread: number; seenAt: number } {
  const bundle = useMission((s) => s.bundle);
  const alerts = useMission((s) => s.record?.alerts) ?? NO_ALERTS;
  const viewer = useAlertViewer();
  const missionId = bundle?.mission.id ?? '';
  const seenAt = useAlertSeen((s) => s.seen[missionId] ?? 0);
  const items = useMemo(() => (viewer && bundle ? buildAlertFeed(alerts, bundle.alertRules, viewer) : []), [alerts, bundle, viewer]);
  const unread = useMemo(() => unreadImportant(items, seenAt, viewer?.userId ?? null), [items, seenAt, viewer]);
  return { items, viewer, unread, seenAt };
}
